import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, CardContent, Grid, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { apiConstants, postGeneric } from "../../common/constants";
import { FormProvider, RHFSelectField, RHFTextField } from "../../common/hook-form";
import PageTitle from "../../components/PageTitle";
import { orderStatusOptions } from "../../helpers/UtilityHelper";
import { formatDate1 } from "../../common/util";
import RHFDateTimeField from "../../common/hook-form/RHFDateTimeField";
import { FieldLabel } from "../../common/FilterBar";

function ManualStatusUpdate() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const methods = useForm({
        defaultValues: {
            currentLocation: "",
            remark: "",
            waybill: "",
            orderStatusCode: '',
            dateTime: null,
            status: ""
        }
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const selectedStatus = orderStatusOptions.find(status => status.name === data.status);
            const statusCode = selectedStatus ? selectedStatus.code : null;
            const payload = {
                "waybill": data.waybill,
                "orderStatusDescription": data.status,
                "orderStatusCode": statusCode,
                "currentLocation": data.currentLocation,
                "Time": formatDate1(data.dateTime, 'HH:mm:ss'),
                "remark": data.remark,
                "Date": formatDate1(data.dateTime, 'yyyy-MM-dd'),
                "skipMerchantWebhooks": false,
                "timestamp": `${formatDate1(data.dateTime, 'yyyy-MM-dd')}T${formatDate1(data.dateTime, 'HH:mm:ss')}Z`
            };
            const response = await postGeneric(apiConstants.MANUAL_STATUS, payload);
            if (response.status === 200 && response.data.status === 'SUCCESS') {
                enqueueSnackbar('Status updated successfully', { variant: 'success' });
                reset();
            } else {
                enqueueSnackbar(response?.data?.responseObj || 'Status update failed', { variant: 'error' });
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar('Status update failed', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <PageTitle>Manual Order Status Update</PageTitle>

            <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                {/* Card header */}
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>
                        Order Status Update
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>
                        Update delivery status for individual orders manually
                    </Typography>
                </Box>

                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2.5}>

                            <Grid item xs={12} sm={6} md={4}>
                                <RHFTextField name="waybill" label="AWB Number" placeholder="Enter AWB number" fullWidth required />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <RHFDateTimeField name="dateTime" label="Date & Time" maxDateTime={new Date()} required />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <RHFSelectField
                                    name="status"
                                    label="Order Status"
                                    width="100%"
                                    options={orderStatusOptions.map(op => ({ label: op.name, value: op.name }))}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <RHFTextField name="currentLocation" label="Current Location" placeholder="e.g. Mumbai Hub" fullWidth />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <RHFTextField name="remark" label="Remark" placeholder="Optional remark" fullWidth  />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        variant="contained"
                                        size="small"
                                        type="submit"
                                        disableElevation
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                                    >
                                        Submit
                                    </LoadingButton>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disableElevation
                                        onClick={() => reset()}
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
        </>
    );
}

export default ManualStatusUpdate;
